import React, { useContext, useId } from "react"
import { ColorPickerStateContext } from "react-aria-components/ColorPicker"
import { ColorArea } from "react-aria-components/ColorArea"
import {
  ColorSlider,
  ColorThumb,
  SliderTrack
} from "react-aria-components/ColorSlider"
import { Label } from "react-aria-components/Label"
import { cn } from "../utils"
import { VisuallyHidden } from "../visually-hidden"
import { TRANSPARENT, toColor, toValue } from "./use-color-value"

// =============================================================================
// Styles
// =============================================================================

const focusRing =
  "outline-2 outline-offset-2 outline-transparent focus-visible:outline-gray-900"

// Grows on focus, with a dark edge around the white border so it shows on
// light and dark colors alike
const areaThumbStyles = [
  "size-2.5 rounded-full border-2 border-white shadow",
  "outline-1 outline-transparent",
  "data-[focus-visible]:size-4 data-[focus-visible]:outline-gray-900"
].join(" ")

// The picker's transparent tile, as the old picker drew it
const transparentTilePattern: React.CSSProperties = {
  backgroundColor: "#EEE",
  backgroundImage:
    "conic-gradient(rgba(0, 0, 0, 0.25) 25%, transparent 0 50%, rgba(0, 0, 0, 0.25) 0 75%, transparent 0)",
  backgroundSize: "8px 8px"
}

// Mirrors the picker's original triangle pointers
function HueThumb() {
  return (
    <ColorThumb
      className="left-1/2 h-3 w-full rounded-sm data-[focus-visible]:outline-2 data-[focus-visible]:outline-offset-2 data-[focus-visible]:outline-gray-900"
      // ColorThumb sets the current color inline, which a class can't override
      style={{ backgroundColor: "transparent" }}
    >
      <div className="absolute inset-0 flex justify-between">
        <div className="size-0 border-[0.375rem] border-transparent border-l-white" />
        <div className="size-0 border-[0.375rem] border-transparent border-r-white" />
      </div>
    </ColorThumb>
  )
}

// =============================================================================
// Component
// =============================================================================

export type ColorPanelPickerProps = {
  /** Offers a "transparent" tile above the hue slider */
  transparency?: boolean
}

/**
 * The picker itself: saturation and brightness area and hue slider. Must be
 * rendered inside a `ColorPanel`; compose `ColorPanel.SavedColors` and
 * `ColorPanel.DesignColors` after it for the swatch sections.
 */
export function ColorPanelPicker({ transparency }: ColorPanelPickerProps) {
  const state = useContext(ColorPickerStateContext)
  const areaNameId = useId()

  if (!state) return null

  const select = (next: string) => state.setColor(toColor(next))
  const current = toValue(state.color)

  return (
    <div data-slot="color-panel-picker" className="flex flex-col">
      <VisuallyHidden id={areaNameId}>Saturation and brightness</VisuallyHidden>
      <div className="flex w-full">
        <ColorArea
          colorSpace="hsb"
          xChannel="saturation"
          yChannel="brightness"
          aria-labelledby={areaNameId}
          className="relative mr-2 h-40 flex-auto cursor-pointer rounded"
        >
          <ColorThumb className={areaThumbStyles} />
        </ColorArea>
        <div className="grid w-6 grid-rows-6">
          {transparency && (
            <button
              type="button"
              aria-pressed={current === TRANSPARENT}
              className={cn("row-span-1 size-6 rounded", focusRing)}
              style={transparentTilePattern}
              onClick={() => select(TRANSPARENT)}
            >
              <VisuallyHidden>Transparent</VisuallyHidden>
            </button>
          )}
          <ColorSlider
            channel="hue"
            colorSpace="hsb"
            orientation="vertical"
            className={cn(
              "relative",
              transparency ? "row-span-5" : "row-span-6"
            )}
          >
            {/* The slider's input only takes its name from its own Label */}
            <Label className="sr-only">Hue</Label>
            <SliderTrack className="h-full w-6 cursor-pointer rounded">
              <HueThumb />
            </SliderTrack>
          </ColorSlider>
        </div>
      </div>
    </div>
  )
}

ColorPanelPicker.displayName = "ColorPanel.Picker"
